import { router } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MoreHorizontal } from 'lucide-react'
import { OverdueBadge } from '@/components/OverdueBadge'
import type CrmFundraisingInstallment from '@/types/serializers/CrmFundraisingInstallment'
import {
  INSTALLMENT_STATUS_LABELS,
  STATUS_BADGE_VARIANT,
} from '@/lib/fundraising-labels'

interface InstallmentTableProps {
  installments: CrmFundraisingInstallment[]
  recurringDonationId: number
  /** When set (rendered in a sheet), refetch the sheet after an installment change. */
  onMutated?: () => void
}

export function InstallmentTable({ installments, recurringDonationId, onMutated }: InstallmentTableProps) {
  const mutationOpts = onMutated
    ? { preserveScroll: true, preserveState: true, onSuccess: () => onMutated() }
    : {}

  function handleMarkReceived(installmentId: number) {
    router.patch(
      `/fundraising/recurring_donations/${recurringDonationId}/installments/${installmentId}`,
      { installment: { status: 'received', received_at: new Date().toISOString() } } as Record<string, unknown>,
      mutationOpts
    )
  }

  function handleCancel(installmentId: number) {
    router.patch(
      `/fundraising/recurring_donations/${recurringDonationId}/installments/${installmentId}`,
      { installment: { status: 'cancelled' } } as Record<string, unknown>,
      mutationOpts
    )
  }

  if (installments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Aucun versement pour ce don recurrent.
      </p>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date d'echeance</TableHead>
          <TableHead>Montant</TableHead>
          <TableHead>Statut</TableHead>
          <TableHead>Retard</TableHead>
          <TableHead className="w-[50px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {installments.map((installment) => {
          const statusKey = (installment as any).status_key ?? (typeof installment.status === 'object' ? '' : installment.status)
          const statusLabel = typeof installment.status === 'object' ? installment.status.label : String(installment.status)
          const isExpected = statusKey === 'expected'

          return (
            <TableRow key={installment.id}>
              <TableCell>
                {new Date(installment.due_date).toLocaleDateString('fr-FR')}
              </TableCell>
              <TableCell className="tabular-nums">
                {installment.amount_formatted}
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_BADGE_VARIANT[statusKey] ?? 'outline'}>
                  {statusLabel}
                </Badge>
              </TableCell>
              <TableCell>
                {installment.overdue_tier ? (
                  <OverdueBadge tier={installment.overdue_tier} />
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                {isExpected && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleMarkReceived(installment.id)}>
                        Marquer recu
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleCancel(installment.id)}
                      >
                        Annuler
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
